import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Heart,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingUp
} from 'lucide-react';
import CustomSelect from './ui/CustomSelect';
import BlogNavbar from './BlogNavbar';
import { deleteMyBlog, fetchMyBlogs, fetchMyBlogsSummary, formatCompactMetric } from '../services/blogService';

const parseCompactNumber = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  const numericPart = parseFloat(normalized.replace(/[^0-9.]/g, ''));
  if (Number.isNaN(numericPart)) return 0;
  if (normalized.endsWith('K')) return numericPart * 1000;
  if (normalized.endsWith('M')) return numericPart * 1000000;
  return numericPart;
};

const statusClasses = {
  published: 'bg-[#6f36ff] text-white',
  pending: 'bg-[#ffb84d] text-[#6d4100]',
  draft: 'bg-[#eef2ff] text-[#4452a8]',
  rejected: 'bg-[#fff1f3] text-[#c84765]'
};

const tagClasses = {
  published: 'bg-[#f3ebff] text-[#8c54d8]',
  pending: 'bg-[#fff1de] text-[#b56a00]',
  draft: 'bg-[#eef2ff] text-[#4452a8]',
  rejected: 'bg-[#fff1f3] text-[#c84765]'
};

const STATUS_FILTER_MAP = {
  All: 'all',
  Published: 'published',
  Pending: 'pending_approval',
  Drafts: 'draft',
  Rejected: 'rejected'
};

const SORT_OPTIONS = [
  { value: 'Latest', label: 'Latest' },
  { value: 'Most Viewed', label: 'Most Viewed' },
  { value: 'Most Liked', label: 'Most Liked' },
  { value: 'Most Commented', label: 'Most Commented' }
];

const MyBlogsPage = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('Latest');
  const [blogItems, setBlogItems] = useState([]);
  const [summary, setSummary] = useState({
    totalBlogs: 0, draftBlogs: 0, pendingBlogs: 0,
    publishedBlogs: 0, rejectedBlogs: 0,
    totalLikes: 0, totalComments: 0, totalViews: 0, averageEngagementRate: 0
  });
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const [summaryData, blogsData] = await Promise.all([
          fetchMyBlogsSummary(),
          fetchMyBlogs({ page, limit: 5, status: STATUS_FILTER_MAP[activeTab], search: debouncedSearchQuery })
        ]);
        if (!isMounted) return;
        setSummary(summaryData);
        setBlogItems(blogsData.items);
        setPageCount(blogsData.pagination.totalPages || 1);
        setError('');
      } catch (fetchError) {
        if (isMounted) { setBlogItems([]); setError(fetchError.message || 'Failed to load your blogs'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [activeTab, page, debouncedSearchQuery]);

  const stats = useMemo(() => [
    { label: 'Total Blogs', value: formatCompactMetric(summary.totalBlogs), subtext: `${summary.publishedBlogs} Published • ${summary.pendingBlogs} Pending`, icon: FileText, iconColor: 'text-[#9a51a0]', cardStyle: 'border-purple-100 bg-gradient-to-br from-[#f7f1ff] via-white to-[#efe5ff]' },
    { label: 'Total Likes', value: formatCompactMetric(summary.totalLikes), subtext: `${summary.publishedBlogs} published blogs`, icon: Heart, iconColor: 'text-[#ef4f9f]', cardStyle: 'border-pink-100 bg-gradient-to-br from-[#fff1f8] via-white to-[#ffe7f2]' },
    { label: 'Comments', value: formatCompactMetric(summary.totalComments), subtext: `${summary.pendingBlogs} in review`, icon: MessageSquare, iconColor: 'text-[#f08c2e]', cardStyle: 'border-orange-100 bg-gradient-to-br from-[#fff7ef] via-white to-[#ffefdf]' },
    { label: 'Total Views', value: formatCompactMetric(summary.totalViews), subtext: `${summary.draftBlogs} drafts`, icon: Eye, iconColor: 'text-[#4d7cff]', cardStyle: 'border-blue-100 bg-gradient-to-br from-[#f3f7ff] via-white to-[#e8f0ff]' },
    { label: 'Engagement', value: `${summary.averageEngagementRate.toFixed(1)}%`, subtext: summary.averageEngagementRate >= 5 ? 'Strong' : 'Growing', icon: TrendingUp, iconColor: 'text-[#9956f6]', cardStyle: 'border-violet-100 bg-gradient-to-br from-[#f8f3ff] via-white to-[#f1e9ff]' }
  ], [summary]);

  const sortedBlogs = useMemo(() => {
    const sorted = [...blogItems];
    if (sortBy === 'Most Viewed') sorted.sort((a, b) => parseCompactNumber(b.views) - parseCompactNumber(a.views));
    else if (sortBy === 'Most Liked') sorted.sort((a, b) => parseCompactNumber(b.likes) - parseCompactNumber(a.likes));
    else if (sortBy === 'Most Commented') sorted.sort((a, b) => parseCompactNumber(b.comments) - parseCompactNumber(a.comments));
    else sorted.sort((a, b) => new Date(b.raw?.updatedAt || 0).getTime() - new Date(a.raw?.updatedAt || 0).getTime());
    return sorted;
  }, [blogItems, sortBy]);

  const tabCounts = useMemo(() => ({
    All: summary.totalBlogs, Published: summary.publishedBlogs,
    Pending: summary.pendingBlogs, Drafts: summary.draftBlogs, Rejected: summary.rejectedBlogs
  }), [summary]);

  const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); };
  const pageButtons = Array.from({ length: pageCount }, (_, i) => i + 1);

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog? This cannot be undone.')) return;
    try {
      setDeletingId(blogId);
      await deleteMyBlog(blogId);
      setBlogItems((prev) => prev.filter((b) => String(b._id) !== String(blogId)));
      setSummary((prev) => ({ ...prev, totalBlogs: Math.max(0, prev.totalBlogs - 1) }));
    } catch (err) {
      setError(err.message || 'Failed to delete blog');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f7f3ff_0%,#f3effc_40%,#f7f5ff_100%)] text-[#1f1730]">
      <BlogNavbar title="My Blogs" subtitle="Track your posts, performance, and publishing workflow" />

      <section className="mx-auto max-w-7xl px-3 pb-10 sm:px-4 lg:px-8">

        {/* Hero Banner */}
        <div className="mb-4 overflow-hidden rounded-2xl border border-white/70 bg-white shadow-sm sm:rounded-[28px]">
          <div className="relative overflow-hidden px-4 py-6 sm:px-8 sm:py-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_left,_rgba(111,54,255,0.2),_transparent_40%),linear-gradient(90deg,rgba(245,239,255,0.98),rgba(255,239,234,0.7))]" />
            <div className="relative">
              <span className="inline-flex rounded-full bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-sm sm:text-[11px]">
                Dashboard
              </span>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-[#20192d] sm:text-3xl lg:text-4xl">
                Your <span className="bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] bg-clip-text text-transparent">Blogs</span>
              </h2>
              <p className="mt-2 max-w-lg text-sm leading-7 text-[#56486a] sm:text-base">
                Track performance, manage posts, and see how your content inspires others.
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="border-t border-[#efe7fb] bg-[linear-gradient(180deg,#f8f4ff,#f4effd)] px-3 py-4 sm:px-6 sm:py-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className={`rounded-2xl border p-3 sm:p-4 ${stat.cardStyle}`}
                  >
                    <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm sm:h-11 sm:w-11 sm:rounded-2xl ${stat.iconColor}`}>
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <p className="mt-3 text-xs font-medium text-slate-600 sm:mt-4">{stat.label}</p>
                    <p className="mt-0.5 text-2xl font-black tracking-tight text-[#1d152b] sm:text-3xl">{stat.value}</p>
                    <p className="mt-1 text-xs font-medium text-emerald-600">{stat.subtext}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Blog List Card */}
        <div className="rounded-2xl border border-white/80 bg-white/80 p-3 shadow-sm backdrop-blur sm:rounded-[28px] sm:p-5">

          {/* Tabs - scrollable on mobile */}
          <div className="-mx-3 overflow-x-auto px-3 sm:-mx-5 sm:px-5" style={{ scrollbarWidth: 'none' }}>
            <div className="flex items-center gap-2 pb-1">
              {['All', 'Published', 'Pending', 'Drafts', 'Rejected'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleTabChange(tab)}
                  className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition-all sm:px-4 sm:py-2.5 sm:text-sm ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] text-white shadow-md'
                      : 'text-[#54456a] hover:bg-[#f5efff]'
                  }`}
                >
                  {tab} ({tabCounts[tab]})
                </button>
              ))}
            </div>
          </div>

          {/* Search + Sort */}
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-[#ece3f8] bg-[#fbf9ff] px-3 py-2.5 shadow-sm">
              <Search className="h-4 w-4 shrink-0 text-[#8d61d7]" />
              <input
                type="text"
                placeholder="Search your blogs..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="w-full bg-transparent text-sm text-[#241b37] outline-none placeholder:text-slate-400"
              />
            </div>
            <div className="w-full sm:w-44">
              <CustomSelect
                id="my-blogs-sort"
                name="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                placeholder="Sort by"
                options={SORT_OPTIONS}
                buttonClassName="px-3 py-2.5 border-[#ece3f8] bg-white text-[#5e4c78] text-sm"
              />
            </div>
          </div>

          {/* Blog Items */}
          <div className="mt-4 space-y-3">
            {error && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error}</div>
            )}

            {loading && (
              <div className="rounded-2xl border border-dashed border-[#e4d8fb] bg-[#fcfaff] px-6 py-10 text-center">
                <p className="font-bold text-[#2a1f3d]">Loading your blogs...</p>
              </div>
            )}

            {!loading && sortedBlogs.map((blog, index) => {
              const primaryActionHref = blog.rawStatus === 'published' && blog.slug
                ? `/blogs/${blog.slug}`
                : `/blogs/write?blogId=${blog._id}`;

              return (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="rounded-2xl border border-[#eee7fb] bg-white p-3 shadow-sm sm:p-4"
                >
                  {/* Mobile Layout */}
                  <div className="flex gap-3 sm:hidden">
                    {/* Cover */}
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#f4ecff]">
                      {blog.image ? (
                        <img src={blog.image} alt={blog.title} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-[#9b55dd]">No Cover</div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusClasses[blog.statusTone]}`}>
                        {blog.status}
                      </span>
                      <h3 className="mt-1 line-clamp-2 text-sm font-bold text-[#1f1730]">{blog.title}</h3>
                      <p className="mt-0.5 text-xs text-[#70627f]">{blog.dateLabel} • {blog.readTime}</p>
                    </div>
                  </div>

                  {/* Mobile Stats Row */}
                  <div className="mt-3 flex items-center gap-2 sm:hidden">
                    <span className="flex items-center gap-1 rounded-full bg-[#fff4f8] px-2.5 py-1 text-xs font-semibold text-[#db4f89]">
                      <Heart className="h-3 w-3" />{blog.likes}
                    </span>
                    <span className="flex items-center gap-1 rounded-full bg-[#f5f1ff] px-2.5 py-1 text-xs font-semibold text-[#8455db]">
                      <MessageSquare className="h-3 w-3" />{blog.comments}
                    </span>
                    <span className="flex items-center gap-1 rounded-full bg-[#f4f8ff] px-2.5 py-1 text-xs font-semibold text-[#4d7cff]">
                      <Eye className="h-3 w-3" />{blog.views}
                    </span>
                  </div>

                  {/* Mobile Actions */}
                  <div className="mt-3 flex gap-2 sm:hidden">
                    <Link to={primaryActionHref} className="flex-1 rounded-xl bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] py-2 text-center text-xs font-semibold text-white shadow-sm">
                      {blog.actionLabel}
                    </Link>
                    {blog.rawStatus !== 'published' && (
                      <Link to={`/blogs/write?blogId=${blog._id}`} className="flex items-center gap-1 rounded-xl border border-[#ece3f8] bg-white px-3 py-2 text-xs font-semibold text-[#5c4a73]">
                        <Pencil className="h-3 w-3" /> Edit
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(blog._id)}
                      disabled={deletingId === blog._id}
                      className="flex items-center gap-1 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-500 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:grid sm:gap-4 xl:grid-cols-[200px_minmax(0,1.3fr)_minmax(200px,0.7fr)_160px] xl:items-center">
                    {/* Cover */}
                    <div className="overflow-hidden rounded-2xl bg-[#f4ecff]">
                      {blog.image ? (
                        <img src={blog.image} alt={blog.title} className="h-[140px] w-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="flex h-[140px] items-center justify-center text-sm font-semibold text-[#9b55dd]">Cover Pending</div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0">
                      <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] ${statusClasses[blog.statusTone]}`}>
                        {blog.status}
                      </span>
                      <h3 className="mt-2 text-lg font-black leading-tight text-[#1f1730] lg:text-xl">{blog.title}</h3>
                      <p className="mt-1 text-sm font-medium text-[#70627f]">{blog.dateLabel} • {blog.readTime}</p>
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#584b6d]">{blog.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {blog.tags.map((tag) => (
                          <span key={tag} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tagClasses[blog.statusTone]}`}>{tag}</span>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 xl:grid-cols-3">
                      <div className="rounded-2xl bg-[#fff8fc] px-2 py-3 text-center">
                        <Heart className="mx-auto h-4 w-4 text-[#ef4f9f]" />
                        <p className="mt-1 text-lg font-black text-[#221830]">{blog.likes}</p>
                        <p className="text-[10px] font-medium text-slate-500">Likes</p>
                      </div>
                      <div className="rounded-2xl bg-[#fbf8ff] px-2 py-3 text-center">
                        <MessageSquare className="mx-auto h-4 w-4 text-[#8455db]" />
                        <p className="mt-1 text-lg font-black text-[#221830]">{blog.comments}</p>
                        <p className="text-[10px] font-medium text-slate-500">Comments</p>
                      </div>
                      <div className="rounded-2xl bg-[#f4f8ff] px-2 py-3 text-center">
                        <Eye className="mx-auto h-4 w-4 text-[#4d7cff]" />
                        <p className="mt-1 text-lg font-black text-[#221830]">{blog.views}</p>
                        <p className="text-[10px] font-medium text-slate-500">Views</p>
                      </div>
                      <div className="col-span-3 rounded-2xl border border-[#e7f4e7] bg-[#f4fcf5] px-3 py-2">
                        <p className="text-xs font-bold text-emerald-700">{blog.insight}</p>
                        <p className="text-[10px] font-medium text-emerald-600">{blog.insightSubtext}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Link to={primaryActionHref} className="rounded-xl bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-4 py-2.5 text-center text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.02]">
                        {blog.actionLabel}
                      </Link>
                      {blog.rawStatus !== 'published' && (
                        <Link to={`/blogs/write?blogId=${blog._id}`} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#ece3f8] bg-white px-4 py-2.5 text-sm font-semibold text-[#5c4a73] hover:bg-[#faf7ff]">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(blog._id)}
                        disabled={deletingId === blog._id}
                        className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deletingId === blog._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {!loading && sortedBlogs.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#e4d8fb] bg-[#fcfaff] px-6 py-10 text-center">
                <p className="font-bold text-[#2a1f3d]">No blogs found</p>
                <p className="mt-1 text-sm text-[#6d5e82]">Try another filter or write a new blog.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <button type="button" onClick={() => setPage((c) => Math.max(1, c - 1))} disabled={page === 1}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#eadffb] bg-white text-[#8d61d7] shadow-sm disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {pageButtons.map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)}
                className={`flex h-9 min-w-[36px] items-center justify-center rounded-xl px-3 text-sm font-semibold shadow-sm ${
                  page === n ? 'bg-gradient-to-r from-[#7f3df1] to-[#d84fa6] text-white' : 'border border-[#eadffb] bg-white text-[#5a4a71]'
                }`}>
                {n}
              </button>
            ))}
            <button type="button" onClick={() => setPage((c) => Math.min(pageCount, c + 1))} disabled={page === pageCount}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#eadffb] bg-white text-[#8d61d7] shadow-sm disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Write New Blog CTA */}
          <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#eadffb] bg-[linear-gradient(90deg,#f3eaff,#f8f2ff)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#7f3df1] to-[#d84fa6] text-white shadow-md">
                <Plus className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-[#221830]">Ready to share something new?</p>
                <p className="text-xs text-[#6a5b7e]">Your next blog could inspire thousands!</p>
              </div>
            </div>
            <Link to="/blogs/write" className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.02]">
              Write New Blog
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyBlogsPage;
